import React, { PropTypes } from 'react';
import idx from 'idx';
import moment from 'moment';
import first from 'lodash/first';
import { v4 } from 'node-uuid';
import ModalDialog from 'common/components/modal/modal_dialog';
import StarRating from 'common/components/star_rating';
import Money from 'common/components/money';
import Thumbnail from 'common/components/thumbnail';
import NegativeFeedbackResponses from 'entries/containers/negative_feedback_responses';

const RevisionImagery = ({ revision }) => {
  const thumbnails = revision.thumbnails || [];
  const thumbnailImage = revision.name? null : first(thumbnails);

  if (!revision.name && !thumbnailImage) {
    return null;
  }

  return (
    <div className="entry-tile">
      <Thumbnail className="tile read-only entry-thumbnail" image={thumbnailImage}>
        { revision.name && <div className="revision-name">{ revision.name }</div> }
      </Thumbnail>
    </div>);
}

const CreateEntryComment = ({ comment, revision, entry }) => (
  <div className="row">
    <div className="small-10 columns">
      <div className="notification-content">
        <h5 className="title">Added a comment to entry #{entry.entry_number}:</h5>
        <p className="content">{ comment.body }</p>
      </div>
    </div>
    <div className="small-6 columns notification-imagery">
      <RevisionImagery revision={revision}/>
    </div>
  </div>
);

const DeleteEntryComment = ({ comment, revision, entry }) => (
  <div className="row">
    <div className="small-10 columns">
      <div className="notification-content">
        <h5 className="title">Deleted a comment from entry #{entry.entry_number}:</h5>
        <p className="content">{ comment.body }</p>
      </div>
    </div>
    <div className="small-6 columns notification-imagery">
      <RevisionImagery revision={revision}/>
    </div>
  </div>
);

const ScoreEntry = ({ revision, entry }) => (
  <div className="row">
    <div className="small-10 columns">
      <div className="notification-content">
        <h5 className="title">Scored entry #{entry.entry_number}</h5>
        <StarRating readonly={true} initialRate={revision.score || 0}/>
      </div>
    </div>
    <div className="small-6 columns notification-imagery">
      <RevisionImagery revision={revision}/>
    </div>
  </div>
);

const GiveEntryFeedback = ({ revision, project, feedback, entry }) => {
  return (
    <div className="row">
      <div className="small-10 columns">
        <div className="notification-content">
          <h5 className="title">Gave feedback to entry #{entry.entry_number}</h5>
          <div className="content">
            <NegativeFeedbackResponses feedback={feedback}
              subcatId={idx(project, _ => _.sub_category.id)} />

            { !!feedback && !!feedback.feedback &&
            <div>
              <div className="sub-title">Feedback: </div>
              <div>{ feedback.feedback }</div>
            </div> }
          </div>
        </div>
      </div>
      <div className="small-6 columns notification-imagery">
        <RevisionImagery revision={revision}/>
      </div>
    </div>
  );
}

const InviteCreative = ({ creative }) => (
  <div className="row">
    <div className="small-16 columns">
      <div className="notification-content">
        <h5 className="title">Invited {creative.username} to participate</h5>
      </div>
    </div>
  </div>
);

const AwardCreative = ({ creative, wrapup }) => (
  <div className="row">
    <div className="small-16 columns">
      <div className="notification-content">
        <h5 className="title">Awarded {creative.username} <Money value={wrapup.award_value}/></h5>
      </div>
    </div>
  </div>
);

const ApproveProofs = ({ wrapup, creative }) => (
  <div className="row">
    <div className="small-16 columns">
      <div className="notification-content">
        <h5 className="title">
          Approved proofs on a <Money value={wrapup.award_value}/> wrap-up with {creative.username}
        </h5>
      </div>
    </div>
  </div>
);

const ApproveFiles = ({ wrapup, creative }) => (
  <div className="row">
    <div className="small-16 columns">
      <div className="notification-content">
        <h5 className="title">
          Approved final files on a <Money value={wrapup.award_value}/> wrap-up with {creative.username}
        </h5>
      </div>
    </div>
  </div>
);

const ApproveName = ({ wrapup, creative }) => (
  <div className="row">
    <div className="small-16 columns">
      <div className="notification-content">
        <h5 className="title">
          Approved name on a <Money value={wrapup.award_value}/> wrap-up with {creative.username}
        </h5>
      </div>
    </div>
  </div>
);

const MAP = {
  create_entry_comment: CreateEntryComment,
  delete_entry_comment: DeleteEntryComment,
  score_entry: ScoreEntry,
  give_entry_feedback: GiveEntryFeedback,
  invite_creative: InviteCreative,
  award: AwardCreative,
  approve_proofs: ApproveProofs,
  approve_final_files: ApproveFiles,
  approve_name: ApproveName
};

const Wrapper = ({ timestamp, context, action_type }) => {
  const NotificationComponent = MAP[action_type];

  return (<div className="notification-wrapper">
    <div className="row">
      <div className="small-16 columns">
        <div className="notification-date">{ moment(timestamp).fromNow() }</div>
      </div>
    </div>
    {NotificationComponent ?
      <NotificationComponent {...context} /> :
      <div>Component for history {action_type} not yet implemented</div>
    }
  </div>);
};

const CollaboratorHistoryModal = ({
  historyId, historyUser, historyList, toggle,
}) => {
  if (!historyId || !historyUser) {
    return null;
  }

  return (<div>
    <ModalDialog
      dialogClasses="collaborator-history-modal"
      modalOpen={!!(historyId && historyUser)}
      closeAction={toggle}
      modalHeader={`${historyUser.username}'s History`}
      primaryLabel="Close"
      primaryClasses="btn btn-tertiary btn-block"
      onPrimary={toggle}
    >
      <div className="collaboratory-history-list">
        { historyList.length === 0 && <h3>This collaborator has no history yet.</h3> }
        { historyList.map(history => (<Wrapper key={v4()} {...history} />)) }
      </div>
    </ModalDialog>
  </div>);
};

Wrapper.propTypes = {
  timestamp: PropTypes.string.isRequired,
  action_type: PropTypes.string.isRequired,
  context: PropTypes.object.isRequired,
};

CollaboratorHistoryModal.propTypes = {
  historyId: PropTypes.number,
  historyUser: PropTypes.object,
  historyList: PropTypes.array,
  toggle: PropTypes.func.isRequired,
};

export default CollaboratorHistoryModal;



// WEBPACK FOOTER //
// ./project/components/collaborator_history.jsx